/**
 * Component: ViewerPage
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R04, BROWSE-V1-R05, BROWSE-V1-R06, BROWSE-V1-R07,
 *               BROWSE-V1-R08, BROWSE-V1-R22
 *
 * Description: BROWSE-V1 guideline viewer page. Reads the guideline
 *              id from the route params, calls
 *              `GET /api/v1/browse/guidelines/{id}` via the feature
 *              api client, and renders:
 *                1. Metadata header (BROWSE-V1-R04)
 *                2. Body: iterate `fullContent.sectionContent[]` in
 *                   source order, one <SectionContent> per entry,
 *                   mounted inside a region with
 *                   data-testid=viewer-body. Source order is
 *                   preserved exactly. (BROWSE-V1-R05)
 *                3. Scroll-to-section: when the URL carries a
 *                   `?sectionId=` query param, scroll to
 *                   `#cnt_<sectionId>` once the body has painted.
 *                   When the param is absent, leave the viewer at
 *                   the top of the page. (BROWSE-V1-R22)
 *
 *              BROWSE-V1-R06 adds the TOC drawer, R07 layers
 *              recommendation colour coding over the placeholder
 *              stub that SectionContent already exposes, and R08
 *              mounts inline citation popovers.
 *
 *              The page is only reachable when `flags.browse` is
 *              true (routes.tsx wraps it in FeatureRoute just like
 *              the R03 list page).
 *
 * Risk Links: R9, R7
 * FMEA Links: FM9, FM7
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ExternalLink,
  FileText,
  Loader2,
  SearchX,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/api';
import { getGuideline } from './api';
import { SectionContent } from './components/SectionContent';
import { TableOfContentsDrawer } from './components/TableOfContentsDrawer';
import { useScrollToSection } from './hooks/useScrollToSection';
import {
  buildReferenceLookup,
  extractRecommendationColourOption,
  extractReferenceContent,
  extractSectionContent,
  type GuidelineDetail,
} from './types';

/**
 * Axios-style error shape. We avoid importing axios types directly
 * because the shared `@/lib/api` wrapper already owns that dependency;
 * this local narrowing just lets the viewer distinguish 404 from
 * other failures without introducing a new coupling.
 */
function extractStatus(err: unknown): number | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number } }).response;
    if (response && typeof response.status === 'number') {
      return response.status;
    }
  }
  return null;
}

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ViewerPage(): React.ReactElement {
  const { guidelineId: guidelineIdParam } = useParams<{ guidelineId: string }>();
  const guidelineId = Number.parseInt(guidelineIdParam ?? '', 10);
  const isValidId = Number.isFinite(guidelineId) && guidelineId > 0;

  // BROWSE-V1-R22: Read the optional ?sectionId= query param and
  // parse it as a positive integer. Anything else (missing, empty,
  // non-numeric, zero/negative) collapses to `null`, which tells
  // useScrollToSection to no-op and the viewer stays at the top.
  //
  // BROWSE-V1-R06: `setSearchParams` is also the seam the TOC
  // drawer uses to drive scrollIntoView — clicking a TOC entry
  // just pushes ?sectionId=<id> and the existing R22 hook reacts.
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionIdRaw = searchParams.get('sectionId');
  const parsedSectionId = sectionIdRaw !== null ? Number.parseInt(sectionIdRaw, 10) : NaN;
  const targetSectionId =
    Number.isFinite(parsedSectionId) && parsedSectionId > 0 ? parsedSectionId : null;

  const [guideline, setGuideline] = useState<GuidelineDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchGuideline = useCallback(async () => {
    if (!isValidId) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const detail = await getGuideline(guidelineId);
      setGuideline(detail);
    } catch (err) {
      if (extractStatus(err) === 404) {
        setNotFound(true);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  }, [guidelineId, isValidId]);

  useEffect(() => {
    void fetchGuideline();
  }, [fetchGuideline]);

  // BROWSE-V1-R05: Narrow the upstream fullContent.sectionContent[]
  // once per detail payload. extractSectionContent preserves source
  // order and drops malformed entries silently; the useMemo keeps
  // the narrowed array identity stable so downstream render passes
  // don't churn.
  const sections = useMemo(
    () => (guideline ? extractSectionContent(guideline) : []),
    [guideline],
  );

  // BROWSE-V1-R07: Read the top-level recommendationColourOption
  // mode selector once and thread it down to every SectionContent /
  // RecommendationCard so all cards in the guideline render under
  // the same mode. Null when the upstream payload omits it.
  const colourOption = useMemo(
    () => (guideline ? extractRecommendationColourOption(guideline) : null),
    [guideline],
  );

  // BROWSE-V1-R08: Build the reference lookup once per payload so
  // every SectionContent uses the same map. Keyed by string
  // referenceId so the anchor-click resolver can look up in O(1).
  const referenceMap = useMemo(() => {
    if (!guideline) return new Map();
    return buildReferenceLookup(extractReferenceContent(guideline));
  }, [guideline]);

  // BROWSE-V1-R22: Drive the scroll-to-section behaviour. The hook
  // waits for `ready` to flip true — i.e. the body has rendered at
  // least one section — before running scrollIntoView on the
  // matching #cnt_<sectionId>. A null targetSectionId keeps the
  // viewer scrolled to top, which is the entry-from-list case.
  useScrollToSection({
    sectionId: targetSectionId,
    ready: !isLoading && !notFound && !error && sections.length > 0,
  });

  // BROWSE-V1-R06: When the TOC drawer picks an entry, push the
  // section id into the URL. The useScrollToSection effect above
  // re-runs on the resulting searchParams change and drives the
  // scroll — there is exactly one scroll code path.
  const handleTocNavigate = useCallback(
    (sectionId: number): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('sectionId', String(sectionId));
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  return (
    <article aria-labelledby="viewer-heading" className="mx-auto max-w-4xl space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/browse">
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
            Back to guidelines
          </Link>
        </Button>
      </div>

      {isLoading && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 text-sm text-muted-foreground"
          data-testid="viewer-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading guideline...
        </div>
      )}

      {!isLoading && notFound && (
        <Alert variant="default" data-testid="viewer-not-found">
          <SearchX className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Guideline not found</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>
              The guideline you requested is not in the local library.
              It may have been removed in the latest sync.
            </span>
            <Button asChild variant="outline" size="sm" className="w-fit">
              <Link to="/browse">Return to the browse list</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !notFound && error && (
        <Alert variant="destructive" data-testid="viewer-error">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Unable to load guideline</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>{error}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void fetchGuideline();
              }}
              className="w-fit"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !notFound && !error && guideline && (
        <>
          <header
            className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
            data-testid="viewer-header"
          >
            <div className="space-y-2">
              <h1 id="viewer-heading" className="text-2xl font-semibold text-foreground">
                {guideline.guidelineName}
              </h1>
              {guideline.shortTitle && guideline.shortTitle !== guideline.guidelineName && (
                <p className="text-base text-muted-foreground">{guideline.shortTitle}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span>
                  Published{' '}
                  <time dateTime={guideline.publishDate}>{formatDate(guideline.publishDate)}</time>
                </span>
                <span>
                  Updated{' '}
                  <time dateTime={guideline.updateDate}>{formatDate(guideline.updateDate)}</time>
                </span>
                {guideline.externalGuidelineURL && (
                  <a
                    href={guideline.externalGuidelineURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label={`Open ${guideline.guidelineName} on the external site in a new tab`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    External source
                  </a>
                )}
              </div>
            </div>
            {/*
              BROWSE-V1-R06: TOC drawer trigger. Only mounted when
              the narrowed sections array has at least one entry -
              an empty guideline has nothing to table-of-contents.
            */}
            {sections.length > 0 && (
              <TableOfContentsDrawer
                sections={sections}
                currentSectionId={targetSectionId}
                onNavigate={handleTocNavigate}
              />
            )}
          </header>

          {/*
            BROWSE-V1-R05: Iterate sections in source order. Array.prototype.map
            preserves the upstream /Get order and extractSectionContent never
            re-sorts. The consumer list uses sectionId as the React key so
            out-of-order or duplicate sectionIds from a broken upstream
            payload still render without React warnings eating real sections.
          */}
          {sections.length > 0 ? (
            <div className="divide-y divide-border" data-testid="viewer-body">
              {sections.map((section, index) => (
                <SectionContent
                  key={`${section.sectionId}-${index}`}
                  section={section}
                  colourOption={colourOption}
                  referenceMap={referenceMap}
                />
              ))}
            </div>
          ) : (
            <div
              className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border p-8 text-center"
              data-testid="viewer-empty-body"
            >
              <FileText className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm font-medium text-foreground">No section content</p>
              <p className="text-xs text-muted-foreground">
                This guideline has no renderable sections in the current sync snapshot.
              </p>
            </div>
          )}
        </>
      )}
    </article>
  );
}

export default ViewerPage;
